import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import Nav from "@/components/Nav";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      nav(u.role === "admin" ? "/admin" : "/mon-planning");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/mon-planning';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      <Nav />
      <div className="mx-auto max-w-md px-6 py-24">
        <p className="mb-4 text-xs font-medium uppercase tracking-[0.32em] text-stone-500">Espace membre</p>
        <h1 className="font-display mb-10 text-4xl font-light tracking-tight text-stone-900">Connexion</h1>
        
        {/* Google Sign-In Button - Primary Option */}
        <Button 
          type="button" 
          onClick={handleGoogleLogin}
          className="w-full rounded-full bg-[#FFFFFF] py-6 text-black shadow-lg hover:bg-[#F5F5F5] border-2 border-stone-200"
        >
          <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continuer avec Google
        </Button>
        
        {/* Divider */}
        <div className="relative py-8">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-stone-300"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-[#F9F9F6] px-4 text-sm font-medium text-stone-500">OU</span>
          </div>
        </div>
        
        {/* Info Block - Between Divider and Form */}
        <div className="mb-6 rounded-lg bg-stone-100 p-4 text-center">
          <p className="text-sm text-stone-700">
            ℹ️ Les comptes sont créés exclusivement par l&apos;administrateur après souscription d&apos;un forfait.
          </p>
          <p className="mt-2 text-xs text-stone-500">
            Pour devenir membre, contactez Samuel : <a href="tel:0668497750" className="underline">06.68.49.77.50</a>
          </p>
        </div>
        
        {/* Classic Email/Password Form - Secondary Option */}
        <form onSubmit={submit} className="space-y-5 opacity-75">
          <p className="text-center text-xs uppercase tracking-wider text-stone-500">Connexion classique</p>
          <div>
            <Label htmlFor="email" className="text-xs text-stone-600">Email</Label>
            <Input 
              data-testid="login-email" 
              id="email" 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
              className="mt-1.5 h-11 bg-white text-sm"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs text-stone-600">Mot de passe</Label>
            <Input 
              data-testid="login-password" 
              id="password" 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              required 
              className="mt-1.5 h-11 bg-white text-sm"
            />
          </div>
          {error && <p data-testid="login-error" className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
          <Button 
            data-testid="login-submit" 
            type="submit" 
            disabled={loading} 
            className="w-full rounded-full bg-stone-800 py-5 text-sm hover:bg-stone-900"
          >
            {loading ? "Connexion…" : "Se connecter"}
          </Button>
        </form>
      </div>
    </div>
  );
}
